import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { SchoolContextService } from './school-context.service';

@Injectable({
  providedIn: 'root'
})
export class DynamicLabelService {
  constructor(private schoolContextService: SchoolContextService) {}

  // Subject/Course labels
  get subjectLabel(): Observable<string> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.labels.subject || 'Subject')
    );
  }

  get courseLabel(): Observable<string> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.labels.subject || 'Subject')
    );
  }

  // Student labels
  get studentLabel(): Observable<string> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.labels.student || 'Student')
    );
  }

  get learnerLabel(): Observable<string> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.labels.student || 'Student')
    );
  }

  // Evaluation labels
  get evaluationLabel(): Observable<string> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.labels.evaluation || 'Evaluation')
    );
  }

  get assessmentLabel(): Observable<string> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.labels.evaluation || 'Evaluation')
    );
  }

  // Term/Semester labels
  get termLabel(): Observable<string> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.labels.term || 'Term')
    );
  }

  get semesterLabel(): Observable<string> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.labels.semester || 'Semester')
    );
  }

  // Registration labels
  get courseRegistrationLabel(): Observable<string> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.labels.courseRegistration || 'Course Registration')
    );
  }

  get studentRegistrationLabel(): Observable<string> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.labels.studentRegistration || 'Student Registration')
    );
  }

  // Academic structure labels
  get academicLevelLabel(): Observable<string> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.labels.academicLevel || 'Academic Level')
    );
  }

  get sequenceLabel(): Observable<string> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.labels.sequence || 'Sequence')
    );
  }

  get departmentLabel(): Observable<string> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.labels.department || 'Department')
    );
  }

  get specializationLabel(): Observable<string> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.labels.specialization || 'Specialization')
    );
  }

  // Feature flags
  get requiresCourseRegistration(): Observable<boolean> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.features.requiresCourseRegistration || false)
    );
  }

  get requiresStudentRegistration(): Observable<boolean> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.features.requiresStudentRegistration || false)
    );
  }

  get usesSemesters(): Observable<boolean> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.features.usesSemesters || false)
    );
  }

  get usesTerms(): Observable<boolean> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.features.usesTerms || false)
    );
  }

  get usesSequences(): Observable<boolean> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.features.usesSequences || false)
    );
  }

  // Convenience methods for common checks
  get isUniversity(): Observable<boolean> {
    return this.schoolContextService.getCurrentSchoolType().pipe(
      map(type => type === 'UNIVERSITY' || type === 'HIGHER')
    );
  }

  get isSecondarySchool(): Observable<boolean> {
    return this.schoolContextService.getCurrentSchoolType().pipe(
      map(type => type === 'SECONDARY')
    );
  }

  get isPrimarySchool(): Observable<boolean> {
    return this.schoolContextService.getCurrentSchoolType().pipe(
      map(type => type === 'PRIMARY')
    );
  }

  // Get all labels as an object for easy use in templates
  getAllLabels(): Observable<{
    subject: string;
    student: string;
    evaluation: string;
    term: string;
    courseRegistration: string;
    studentRegistration: string;
    academicLevel: string;
    sequence: string;
    semester: string;
    department: string;
    specialization: string;
  }> {
    return this.schoolContextService.getCurrentSchoolConfig().pipe(
      map(config => config?.labels || {
        subject: 'Subject',
        student: 'Student',
        evaluation: 'Evaluation',
        term: 'Term',
        courseRegistration: 'Course Registration',
        studentRegistration: 'Student Registration',
        academicLevel: 'Academic Level',
        sequence: 'Sequence',
        semester: 'Semester',
        department: 'Department',
        specialization: 'Specialization'
      })
    );
  }
}
